import { IGenericObject, ITreeItem } from 'types';

export const getLocalized = (
  value: any,
  path: string,
  defaultValue: any,
  defaultLocale = 'en-US'
): any => {
  try {
    return value[path.split('[]')[0]].find((x) => x.culture === defaultLocale).value;
  } catch (e) {
    return defaultValue;
  }
};

export const get = (value: any, path: string, defaultValue: any): any => {
  return String(path)
    .split('.')
    .reduce((acc, v) => {
      try {
        acc = acc[v] !== undefined && acc[v] !== null ? acc[v] : defaultValue;
      } catch (e) {
        return defaultValue;
      }
      return acc;
    }, value);
};

export const withoutProps = (obj: any, keys: string[]): Record<string, unknown> => {
  const target: Record<string, unknown> = {};
  for (const i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
};

export const deepSearch = (
  node: ITreeItem,
  term: string,
  searchField: string,
  idField: string,
  foundIDS: string[],
  expandIDS: string[],
  expandLevel: number
) => {
  const value = node[searchField] || '';
  let isMatching = value.toString().toLowerCase().indexOf(term.toLowerCase()) > -1;

  if (Array.isArray(node.children)) {
    node.children.forEach((child) => {
      const hasMatchingChild = deepSearch(
        child,
        term,
        searchField,
        idField,
        foundIDS,
        expandIDS,
        expandLevel + 1
      );
      isMatching = isMatching || hasMatchingChild;
    });
  }

  const nodeIdentifier = node[idField];

  if (isMatching && nodeIdentifier) {
    foundIDS.push(nodeIdentifier);

    if (
      expandLevel < 3 &&
      !expandIDS.includes(nodeIdentifier) &&
      Array.isArray(node.children) &&
      node.children.length > 0
    ) {
      expandIDS.push(nodeIdentifier);
    }
  }

  return isMatching;
};

export const filterTree = <T extends ITreeItem>(
  data: T[],
  matchedIDS: string[],
  idField: string
): T[] => {
  return data
    .filter((item) => matchedIDS.indexOf(item[idField]) > -1)
    .map((item) => ({
      ...item,
      children: item.children ? filterTree(item.children, matchedIDS, idField) : [],
    }));
};

export const treeSearch = <T extends ITreeItem>(
  term: string,
  data: T[],
  searchField: string,
  idField: string
): { nodes: T[]; expandIDS: string[] } => {
  const matchedIDS: string[] = [];
  const expandIDS: string[] = [];

  data.forEach((dataNode) =>
    deepSearch(dataNode, term, searchField, idField, matchedIDS, expandIDS, 0)
  );

  return { nodes: filterTree(data, matchedIDS, idField), expandIDS };
};

export const getNodeFromTree = <T extends ITreeItem>(
  data: T[],
  value: string,
  field: string,
  parents?: string[]
): { node?: T | ITreeItem; nodeParents?: string[] } | null => {
  let i;
  let result: any = null;
  const nodeParents = [...(parents || [])];

  for (i = 0; result === null && i < data.length; i++) {
    const node = data[i];
    if (node[field] === value) {
      if (node.parentId) {
        nodeParents.push(node.parentId);
      }
      result = { node, nodeParents: nodeParents };
    } else if (Array.isArray(node.children) && node.children.length > 0) {
      result = getNodeFromTree(node.children, value, field, nodeParents);
    }
  }
  return result;
};

export const convertTreeToFlat = <T extends ITreeItem>(
  data: T[],
  identifier: string
): Record<string, T> => {
  let i;
  let result = {};
  for (i = 0; i < data.length; i++) {
    const node = data[i];
    const nodeIdentifier = node[identifier];

    result = { ...result, [nodeIdentifier]: node };

    if (Array.isArray(node.children)) {
      result = { ...result, ...convertTreeToFlat(node.children, identifier) };
    }
  }
  return result;
};

export const getProperty = (propertyName: string, object: { [key: string]: any }): any => {
  const parts = propertyName.split('.');
  const length = parts.length;
  let property = object || undefined;

  for (let i = 0; i < length; i++) {
    try {
      property = property[parts[i]];
    } catch (error) {
      return property;
    }
  }

  return property;
};

export const cleanObject = (obj: IGenericObject): IGenericObject => {
  for (const propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
      delete obj[propName];
    }
  }
  return obj;
};
